import React, { useState } from 'react';
import styled from 'astroturf/react';

import ExpandLessIcon from 'js/design_system/icons/expand_less.svg';

type Section = {
  title: string | React.ReactNode;
  contents: React.ReactNode;
  defaultOpen?: boolean;
};

type AccordionProps = {
  sections: Section[];
  style?: React.CSSProperties;
};

type AccordionSectionProps = {
  section: Section;
  open: boolean;
  onToggle: () => void;
  style?: React.CSSProperties;
};

const SectionWrapper = styled.div`
  display: flex;
  padding: var(--spacing-xs-16, 16px) 0px;
  flex-direction: column;
  justify-content: center;
  gap: var(--spacing-xs-16, 16px);
  width: 100%;

  &:not(:last-child) {
    border-bottom: 1px solid var(--stroke-light, #dddde2);
  }
`;

const SectionTitle = styled.button`
  appearance: none;
  padding: 0;
  border: 0;
  background: transparent;
  cursor: pointer;
  width: 100%;

  display: flex;
  align-items: center;
  justify-content: space-between;

  &:hover {
    color: var(--hover, #4960d1);
    text-decoration: underline;
  }
`;

const ExpandIcon = styled.div<{ open: boolean }>`
  transform: rotate(180deg);
  color: var(--text-primary, #070a15);

  &.open {
    transform: rotate(0deg);
  }
`;

const SectionTitleText = styled.div`
  @import 'stylesheets/design_system/font.scss';

  @include body__small-bold;
  text-align: start;
`;

const SectionContents = styled.div`
  @import 'stylesheets/design_system/font.scss';

  @include body__small;
`;

const SectionsWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  align-self: stretch;
  border-top: 1px solid var(--stroke-light, #dddde2);
  border-bottom: 1px solid var(--stroke-light, #dddde2);
`;

export const AccordionSection: React.FC<AccordionSectionProps> = ({
  section,
  open,
  onToggle,
  style,
}) => {
  return (
    <SectionWrapper style={style}>
      <SectionTitle onClick={onToggle}>
        {typeof section.title === 'string' ? (
          <SectionTitleText>{section.title}</SectionTitleText>
        ) : (
          section.title
        )}
        <ExpandIcon open={open}>
          <ExpandLessIcon />
        </ExpandIcon>
      </SectionTitle>
      {open && <SectionContents>{section.contents}</SectionContents>}
    </SectionWrapper>
  );
};

export const Accordion: React.FC<AccordionProps> = ({ sections, style }) => {
  const [openSections, setOpenSections] = useState<boolean[]>(() =>
    sections.map((section) => !!section.defaultOpen),
  );

  function toggleIndex(i: number) {
    setOpenSections((prev) => {
      const next = [...prev];
      next[i] = !next[i];
      return next;
    });
  }

  return (
    <SectionsWrapper style={style}>
      {sections.map((section, i) => (
        <AccordionSection
          key={`accordion-section-${i}`}
          open={openSections[i]}
          onToggle={() => toggleIndex(i)}
          section={section}
        />
      ))}
    </SectionsWrapper>
  );
};
